import { PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js"
import { createFileRoute, redirect } from "@tanstack/react-router"
import * as InFlowEnums from "ipctg-inflow-types/InFlowEnums"
import { FormEvent, useState } from "react"
import { useTranslation } from "react-i18next"

import api from "@/utilities/api"

import { Button } from "@/globalComponents/atoms/Button"
import Card from "@/globalComponents/atoms/Card"
import FieldError from "@/globalComponents/atoms/FieldError"
import Heading from "@/globalComponents/atoms/Heading"
import Separator from "@/globalComponents/atoms/Separator"
import Text from "@/globalComponents/atoms/Text"
import StripeElementsWrapper from "@/globalComponents/molecules/StripeElementsWrapper"
import { ExpectedError } from "@/utilities/expectedErrors"
import { parkPaymentIntentElementsQueryOptions } from "@/utilities/hooks/queries/park"
import { confirmPassesPurchaseQueryOptions } from "@/utilities/hooks/queries/passes"
import { meQueryOptions } from "@/utilities/hooks/queries/user"
import { useStripePaymentElement } from "@/utilities/hooks/useStripePaymentElement"
import { log } from "@/utilities/logging"
import { prettyCurrencyNumber } from "@/utilities/normalizers"

interface FlexCheckoutSearch {
  rateID: string
}

export const Route = createFileRoute(
  "/qr/$qrLookupID/passes/$epoch/purchase/checkout"
)({
  staticData: {
    pageName: "Checkout",
  },
  validateSearch: (search: Record<string, unknown>): FlexCheckoutSearch => ({
    rateID: (search.rateID as string) || "",
  }),
  beforeLoad: async ({ params, context, search }) => {
    // Get user info to determine contact method
    const me = await context.queryClient.ensureQueryData(meQueryOptions())

    // Now call /payment-intent-elements (requires CheckoutDetails to be set)
    const paymentIntentRsp = await context.queryClient.fetchQuery(
      parkPaymentIntentElementsQueryOptions({
        email: me?.cognitoUser.email || null,
        phone: me?.cognitoUser.phone_number || null,
        contactType: me?.cognitoUser.phone_number
          ? InFlowEnums.ContactType.Phone
          : InFlowEnums.ContactType.Email,
        epoch: params.epoch,
        rateID: search.rateID || "",
      })
    )

    // If no payment required, redirect to confirmation which will handle the purchase
    if (!paymentIntentRsp.requiresPayment) {
      throw redirect({
        to: "/qr/$qrLookupID/passes/$epoch/purchase/confirmation",
        params: {
          qrLookupID: params.qrLookupID,
          epoch: params.epoch,
        },
      })
    }

    return {
      paymentIntent: paymentIntentRsp,
    }
  },
  component: RouteComponent,
})

function RouteComponent() {
  const navigate = Route.useNavigate()
  const { qrLookupID, epoch } = Route.useParams()
  const { paymentIntent } = Route.useRouteContext()
  const { t } = useTranslation(["park", "common", "errors"])

  return (
    <>
      <Heading className="center" level={3} as={1}>
        {t("cart.checkout", { ns: "park" })}
      </Heading>

      <StripeElementsWrapper
        clientSecret={paymentIntent.clientSecret!}
        customerSessionClientSecret={
          paymentIntent.customerSessionClientSecret || undefined
        }
        flow="passes:purchase"
      >
        <Card>
          <Heading className="center" level={1} as={2}>
            {t("cart.paymentDetails", {
              ns: "park",
            })}
          </Heading>
          <CheckoutForm
            amountDue={paymentIntent.amountDue}
            qrLookupID={qrLookupID}
            goNext={() =>
              navigate({
                to: "/qr/$qrLookupID/passes/$epoch/purchase/confirmation",
                params: { qrLookupID, epoch: epoch },
              })
            }
          />
        </Card>
      </StripeElementsWrapper>
    </>
  )
}

interface CheckoutFormProps {
  amountDue: number
  qrLookupID: string
  goNext: () => void
}

function CheckoutForm(props: CheckoutFormProps) {
  const stripe = useStripe()
  const elements = useElements()
  const { t } = useTranslation(["park", "common", "errors"])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error>()
  const queryClient = Route.useRouteContext().queryClient

  const { paymentElementProps, stripeError } = useStripePaymentElement({
    flow: "passes:purchase",
  })

  if (stripeError) throw stripeError

  return (
    <form onSubmit={handleSubmit}>
      <fieldset disabled={!stripe || !elements || loading}>
        <PaymentElement {...paymentElementProps} />
        <Separator />
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
          }}
        >
          <Heading level={4} as={3}>
            {t("common:cart.dueToday")}
          </Heading>
          <Text level={2} style={{ marginLeft: "var(--space-2)" }}>
            {prettyCurrencyNumber(Math.max(0, props.amountDue))}
          </Text>
        </div>
        {error && (
          <FieldError>
            {error instanceof ExpectedError
              ? t(error.translationKey, { ns: "errors" })
              : error.message}
          </FieldError>
        )}

        <Button type="submit" buttonType="primary" isPending={loading}>
          {t("common.submit", { ns: "common" })}
        </Button>
      </fieldset>
    </form>
  )

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setLoading(true)

    if (!stripe || !elements) {
      setLoading(false)
      return
    }

    const paymentElement = elements.getElement(PaymentElement)
    if (!paymentElement) {
      setError(new Error(t("common.paymentDetailsNotFound", { ns: "errors" })))
      setLoading(false)
      return
    }

    try {
      const result = await stripe.confirmPayment({
        elements,
        redirect: "if_required",
      })

      if (result.error) {
        log({
          level: "error",
          flow: "passes:purchase",
          scope: "payment.stripeConfirmPayment",
          message: "Stripe confirmPayment returned an error",
          meta: {
            code: result.error.code,
            type: result.error.type,
            declineCode: result.error.decline_code,
          },
          sentry: "exception",
          exception: result.error,
        })
        setError(new Error(result.error.message))
        return
      }

      // Wait 1 second to give Stripe time to process
      await new Promise((resolve) => setTimeout(resolve, 1000))

      // Clear any cached purchase-pass data before navigating
      const cognitoIDToken = await api.auth.getCognitoIDToken()
      if (cognitoIDToken) {
        queryClient.removeQueries({
          queryKey:
            confirmPassesPurchaseQueryOptions(cognitoIDToken).queryKey,
        })
      }
      props.goNext()
    } catch (e: unknown) {
      log({
        level: "error",
        flow: "passes:purchase",
        scope: "payment.confirmPayment",
        message: "Passes purchase payment confirmation failed",
        meta: { error: e },
        sentry: e instanceof ExpectedError ? "breadcrumb" : "exception",
        exception: e instanceof Error ? e : new Error(String(e)),
      })
      if (e instanceof Error) {
        setError(e)
      } else {
        setError(new Error(t("common.somethingWentWrong", { ns: "errors" })))
      }
    } finally {
      setLoading(false)
    }
  }
}
